import React, { useState, useEffect } from 'react';
import {
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
  TagIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { clientsAPI, segmentsAPI, Segment } from '../../services/api';
import { Card, CardBody, Button, Badge, Spinner, Modal, EmptyState } from '../ui';

interface ClientSegmentsProps {
  clientId: number;
  onUpdate?: () => void;
}

const ClientSegments: React.FC<ClientSegmentsProps> = ({ clientId, onUpdate }) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [allSegments, setAllSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);

  useEffect(() => {
    fetchClientSegments();
  }, [clientId]);

  const fetchClientSegments = async () => {
    try {
      setLoading(true);
      const data = await clientsAPI.getSegments(clientId);
      setSegments(data);
    } catch (error) {
      console.error('Failed to fetch client segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSegments = async () => {
    try {
      const data = await segmentsAPI.getAll();
      // Filter to only STATIC segments that the client is not already in
      const clientSegmentIds = segments.map(s => s.id);
      const available = data.filter(
        s => s.type === 'STATIC' && !clientSegmentIds.includes(s.id)
      );
      setAllSegments(available);
    } catch (error) {
      console.error('Failed to fetch all segments:', error);
    }
  };

  const handleOpenAddModal = async () => {
    await fetchAllSegments();
    setSelectedSegmentIds([]);
    setShowAddModal(true);
  };

  const handleAddToSegments = async () => {
    if (selectedSegmentIds.length === 0) return;
    
    try {
      setAddLoading(true);
      await clientsAPI.addToSegments(clientId, selectedSegmentIds);
      await fetchClientSegments();
      setShowAddModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to add client to segments:', error);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveFromSegment = async (segmentId: number) => {
    if (!confirm('Remove this client from the segment?')) return;
    
    try {
      await clientsAPI.removeFromSegments(clientId, [segmentId]);
      await fetchClientSegments();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to remove client from segment:', error);
    }
  };

  const toggleSegmentSelection = (segmentId: number) => {
    setSelectedSegmentIds(prev => 
      prev.includes(segmentId)
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId]
    );
  };

  const getSegmentColor = (color?: string) => color || '#6366f1';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-neutral-900">Segments</h3>
          <Badge variant="neutral" size="sm">{segments.length}</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenAddModal}
          leftIcon={<PlusIcon className="h-4 w-4" />}
        >
          Add to Segment
        </Button>
      </div>

      {/* Segments List */}
      {segments.length === 0 ? (
        <div className="text-center py-8 bg-neutral-50 rounded-lg border border-dashed border-neutral-300">
          <TagIcon className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500">
            This client is not in any segments yet.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={handleOpenAddModal}
          >
            Add to a segment
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {segments.map((segment) => (
            <div
              key={segment.id}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 bg-white shadow-sm hover:shadow-md transition-shadow group"
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: getSegmentColor(segment.color) }}
              />
              <span className="text-sm font-medium text-neutral-700">
                {segment.name}
              </span>
              <span className="text-xs text-neutral-400">
                ({segment._count?.clients || 0})
              </span>
              <button
                onClick={() => handleRemoveFromSegment(segment.id)}
                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-error-100 rounded transition-all"
                title="Remove from segment"
              >
                <XMarkIcon className="h-4 w-4 text-error-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add to Segment Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add to Segments"
        subtitle="Select segments to add this client to"
        size="md"
      >
        <div className="space-y-4">
          {allSegments.length === 0 ? (
            <EmptyState
              icon={<FunnelIcon className="h-10 w-10" />}
              title="No available segments"
              description="All static segments already contain this client, or no static segments exist."
            />
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {allSegments.map((segment) => (
                <label
                  key={segment.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedSegmentIds.includes(segment.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSegmentIds.includes(segment.id)}
                    onChange={() => toggleSegmentSelection(segment.id)}
                    className="h-4 w-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                  />
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getSegmentColor(segment.color) }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {segment.name}
                    </p>
                    {segment.description && (
                      <p className="text-xs text-neutral-500 truncate">
                        {segment.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-neutral-400">
                    <UsersIcon className="h-3 w-3" />
                    <span>{segment._count?.clients || 0}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {allSegments.length > 0 && (
            <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddToSegments}
                disabled={selectedSegmentIds.length === 0 || addLoading}
                leftIcon={addLoading ? <Spinner size="sm" /> : <PlusIcon className="h-4 w-4" />}
              >
                {addLoading ? 'Adding...' : `Add to ${selectedSegmentIds.length} Segment${selectedSegmentIds.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ClientSegments;
